import type { DocsOperation, HttpMethod } from '../generated/reference';
import type { TryItValues } from '../schemas/try-it-form-schema';

/** Header parameters the panel fills itself instead of rendering a plain input. */
const MANAGED_HEADERS = new Set(['authorization', 'x-customer-token', 'idempotency-key']);

/** Calls that move money when the store's payments driver is not `dev` (plan 9.2). */
export const PAYMENT_OPERATIONS: ReadonlySet<string> = new Set([
  'createPaymentIntent',
  'completeCheckout',
]);

export const isManagedHeader = (name: string) => MANAGED_HEADERS.has(name.toLowerCase());

/** Declared idempotent, or carries the header by name (plan 4.1: detection by name). */
export function takesIdempotencyKey(operation: DocsOperation): boolean {
  return (
    operation.idempotent ||
    operation.parameters.some(
      (p) => p.in === 'header' && p.name.toLowerCase() === 'idempotency-key',
    )
  );
}

export interface TryItRequest {
  method: HttpMethod;
  url: string;
  headers: Record<string, string>;
  body: string | null;
}

export interface TryItContext {
  baseUrl: string;
  apiKey: string;
  customerToken: string | null;
}

/**
 * The exact request the panel sends, and what "Copy as curl" prints: path params encoded into the
 * path, non-empty query params, the key, the customer token on customer routes, the
 * Idempotency-Key where the operation takes one, and the body compacted to one line.
 */
export function buildTryItRequest(
  operation: DocsOperation,
  values: TryItValues,
  context: TryItContext,
): TryItRequest {
  const path = operation.path.replace(/\{(\w+)\}/g, (_, name: string) =>
    encodeURIComponent(values.path[name]?.trim() ?? ''),
  );
  const query = new URLSearchParams();
  const headers: Record<string, string> = { Authorization: `Bearer ${context.apiKey}` };
  const extraHeaders: Record<string, string> = {};
  for (const param of operation.parameters) {
    if (param.in === 'query') {
      const value = values.query[param.name]?.trim();
      if (value) query.append(param.name, value);
    } else if (param.in === 'header' && !isManagedHeader(param.name)) {
      const value = values.header[param.name]?.trim();
      if (value) extraHeaders[param.name] = value;
    }
  }

  const text = operation.requestBody ? values.body.trim() : '';
  const body = text ? JSON.stringify(JSON.parse(text)) : null;
  if (body !== null) headers['Content-Type'] = 'application/json';
  if (operation.customerToken && context.customerToken) {
    headers['X-Customer-Token'] = context.customerToken;
  }
  const idempotencyKey = values.idempotencyKey.trim();
  if (takesIdempotencyKey(operation) && idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  const search = query.toString();
  return {
    method: operation.method,
    url: `${context.baseUrl.replace(/\/+$/, '')}${path}${search ? `?${search}` : ''}`,
    headers: { ...headers, ...extraHeaders },
    body,
  };
}

export type TryItBody =
  { kind: 'json'; value: unknown } | { kind: 'text'; value: string } | { kind: 'empty' };

export interface TryItResponse {
  status: number;
  statusText: string;
  durationMs: number;
  /** Lower-case names, sorted; only what CORS exposes to the page. */
  headers: Array<[string, string]>;
  body: TryItBody;
}

function readBody(text: string, contentType: string | null): TryItBody {
  if (text === '') return { kind: 'empty' };
  if (contentType?.includes('json')) {
    try {
      return { kind: 'json', value: JSON.parse(text) as unknown };
    } catch {
      return { kind: 'text', value: text };
    }
  }
  return { kind: 'text', value: text };
}

/**
 * A plain `fetch`, not the admin transport: no cookies (`credentials: 'omit'`, even when the API
 * shares the admin's origin), no `X-Nucleus-Client`, so the API sees what a storefront sends.
 * Resolves for every HTTP status; rejects only when no response came back (network, CORS).
 */
export async function sendTryIt(request: TryItRequest): Promise<TryItResponse> {
  const started = performance.now();
  const response = await fetch(request.url, {
    method: request.method,
    headers: request.headers,
    ...(request.body === null ? {} : { body: request.body }),
    credentials: 'omit',
    cache: 'no-store',
  });
  const text = await response.text();
  return {
    status: response.status,
    statusText: response.statusText,
    durationMs: Math.round(performance.now() - started),
    headers: [...response.headers.entries()].sort(([a], [b]) => a.localeCompare(b)),
    body: readBody(text, response.headers.get('content-type')),
  };
}

const shellQuote = (value: string) => `'${value.replaceAll("'", `'\\''`)}'`;

/** The same request as a curl command, values included (plan 9.2 "Copy as curl"). */
export function toCurl(request: TryItRequest): string {
  const lines = [
    request.method === 'GET'
      ? `curl ${shellQuote(request.url)}`
      : `curl -X ${request.method} ${shellQuote(request.url)}`,
    ...Object.entries(request.headers).map(
      ([name, value]) => `-H ${shellQuote(`${name}: ${value}`)}`,
    ),
    ...(request.body === null ? [] : [`--data-raw ${shellQuote(request.body)}`]),
  ];
  return lines.join(' \\\n  ');
}
